// GDriveFolderPreview.tsx
import React, { useEffect, useState } from "react";
import { normalizeDate } from "../../FileUploadTab";
import Loader from "@shared/components/Loader";
import { Tooltip } from "flowbite-react";
import StatusIcon from "../Anomaly/StatusIcon";
import { getFileIcon } from "../../FileUploadTab";
import { ServerFile } from "@shared/interfaces/ProjectsFiles";
import { oneDriveService } from "@services/OneDriveService";
import { gdriveService } from "@services/GdriveService";
import FileTableSkeleton from "./FileTableSkeleton";
import { formatDistanceToNow, parseISO, isValid } from "date-fns";
import { enUS } from "date-fns/locale";
import { DriveFolder } from "@shared/interfaces/CloudFiles";
import DeleteEntityModal from "@pages/Program/components/DeleteEntity";
import { toastService } from "@services/ToastService";

interface DriveFolderPreviewProps {
  folder: DriveFolder;
  projectId: string;
  fetchFolderFiles: (
    projectId: string,
    folderId: string
  ) => Promise<ServerFile[]>;
  loadFile: (id: string, type: string, size: number, filename: string) => void;

  /** Kept in props for compatibility; no longer used here */
  handleCheckboxChange?: (fileId: string) => void;
  handleSelectAll?: (folderId: string, allChecked: boolean) => void;

  checkedItems: string[];
  setCheckedItems: (ids: string[]) => void;

  loadingFileId: string | null;
  activeTaskFileIds: string[];
  handleShowPopup: (fileId: string, file?: ServerFile) => void;
  triggerStatusPopup: (severity: string, anomaly_count: number) => void;
  clearStatusPopup: () => void;
  triggerFolderRefresh: () => void;
  refreshFile: number;
  cloudType?: "onedrive" | "gdrive";
}

const SUFFIX = "-gdrive-folder-file";
const withSuffix = (id: string) => `${id}${SUFFIX}`;

const DriveFolderPreview: React.FC<DriveFolderPreviewProps> = ({
  folder,
  projectId,
  fetchFolderFiles,
  loadFile,
  checkedItems,
  setCheckedItems,
  loadingFileId,
  activeTaskFileIds,
  handleShowPopup,
  triggerStatusPopup,
  clearStatusPopup,
  triggerFolderRefresh,
  refreshFile,
  cloudType = "gdrive",
}) => {
  const apiService = cloudType === "gdrive" ? gdriveService : oneDriveService;
  const cloudIcon =
    cloudType === "gdrive" ? "/icon_google_cloud.svg" : "/icon_ms_onedrive.svg";

  const [files, setFiles] = useState<ServerFile[]>([]);
  const [isOpen, setIsOpen] = useState(false);
  const [hoveredRow, setHoveredRow] = useState<string | null>(null);
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const customLocale = {
    ...enUS,
    formatDistance: (token: any, count: number, options?: any): string => {
      const result = enUS.formatDistance(token, count, options);
      return result.replace(/^about |^over |^almost /, "");
    },
  };

  const getLastSyncedTime = (folderLastSync: string): string => {
    if (!folderLastSync) return "Never synced";
    const parsedDate = parseISO(`${folderLastSync}Z`);
    return isValid(parsedDate)
      ? `${formatDistanceToNow(parsedDate, {
          addSuffix: true,
          locale: customLocale,
        })}`
      : "Invalid date";
  };

  const filteredFiles = files.filter((file) =>
    file.filename.toLowerCase().includes(search.toLowerCase())
  );

  const loadFiles = async () => {
    setLoading(true);
    const fetched = await fetchFolderFiles(projectId, folder.id);
    setFiles(fetched);
    setTimeout(() => setLoading(false), 500);
  };

  const toggleOpen = async () => {
    if (!isOpen && files.length === 0) await loadFiles();
    setIsOpen(!isOpen);
  };

  const handleSync = async () => {
    try {
      setLoading(true);
      await apiService.syncFolder(projectId, folder.id);
      await loadFiles();
      triggerFolderRefresh();
      setIsOpen(true);
    } catch (error) {
      console.error("Failed to sync folder:", error);
    } finally {
      setLoading(false);
    }
  };

  // READY ids within THIS folder (suffixed for selection state)
  const readyIdsWithSuffix = files
    .filter((f) => f.status?.toLowerCase() === "ready")
    .map((f) => withSuffix(f.id));

  // Folder-scoped "all selected?" (compare suffixed IDs)
  const allChecked =
    readyIdsWithSuffix.length > 0 &&
    readyIdsWithSuffix.every((id) => checkedItems.includes(id));

  // Toggle a single (suffixed) id
  const toggleOne = (suffixedId: string) => {
    const isSelected = checkedItems.includes(suffixedId);
    const next = isSelected
      ? checkedItems.filter((x) => x !== suffixedId)
      : [...checkedItems, suffixedId];
    setCheckedItems(next);
  };

  // Toggle all in this folder (ready only) — uses suffixed IDs
  const toggleAll = () => {
    if (allChecked) {
      // remove ALL ready items (suffixed) from the current selection
      const next = checkedItems.filter(
        (id) => !readyIdsWithSuffix.includes(id)
      );
      setCheckedItems(next);
    } else {
      // union current selection with all ready (suffixed) ids
      const union = Array.from(
        new Set([...checkedItems, ...readyIdsWithSuffix])
      );
      setCheckedItems(union);
    }
  };

  const handleFolderDelete = async () => {
    try {
      setDeleting(true);
      await apiService.deleteFolder(projectId, folder.id);
      triggerFolderRefresh();
      setModalOpen(false);
      toastService.success("Folder removed successfully");
    } catch (error) {
      toastService.error("Failed to remove folder. Please try again later.");
      console.error("Failed to delete folder:", error);
    } finally {
      setDeleting(false);
    }
  };

  useEffect(() => {
    if (isOpen) {
      loadFiles();
    }
  }, [refreshFile]);

  return (
    <div className="border-2 border-[#E4E7EC] rounded-lg overflow-hidden mb-4">
      <div className="w-full bg-[#F9FAFB] flex items-center justify-between px-4 py-4">
        <div className="flex items-center space-x-2">
          <img src={cloudIcon} className="w-6" alt="Drive" />
          <h2 className="text-sm 2xl:text-base mt-1 font-semibold text-[#182637]">
            {folder.name} - {folder.owner.first_name} {folder.owner.last_name}
            <span className="ml-1 text-gray-500 font-normal text-xs 2xl:text-sm">
              ({folder.files_count}{" "}
              {folder.files_count === 1 ? "document" : "documents"})
            </span>
          </h2>
        </div>
        <div className="flex items-center space-x-3">
          {isOpen && files.length > 0 && (
            <input
              type="text"
              placeholder="Search document..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="w-72 border border-gray-200 rounded-md px-3 h-8 text-sm text-gray-500 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          )}
          <button
            onClick={handleSync}
            type="button"
            className="text-xs 2xl:text-sm text-gray-500 h-8 flex space-x-1 items-center justify-center hover:text-gray-700 bg-white px-3 border border-gray-200 rounded-md"
          >
            <span>{getLastSyncedTime(folder.last_sync)}</span>
            <img src="/refresh-ccw.svg" className="w-3 h-3" alt="" />
          </button>
          <button
            onClick={() => setModalOpen(true)}
            type="button"
            className="text-xs 2xl:text-sm text-gray-500 h-8 flex space-x-1 items-center justify-center hover:text-gray-700 bg-white px-3 border border-gray-200 rounded-md"
          >
            <img src="/unlink.svg" className="w-4 h-4" alt="" />
          </button>
          <img
            src={
              isOpen
                ? "/arrow_dropdown_black_up.svg"
                : "/arrow_dropdown_black.svg"
            }
            className="w-4 cursor-pointer"
            onClick={toggleOpen}
          />
        </div>
      </div>

      {loading && <FileTableSkeleton />}

      {isOpen && !loading && (
        <>
          {filteredFiles.length === 0 ? (
            <div className="p-4 text-center text-sm text-gray-500">
              {search.length > 0
                ? `No documents found matching "${search}".`
                : "No documents found in this folder."}
            </div>
          ) : (
            <table className="w-full table-auto text-sm 2xl:text-base">
              <thead>
                <tr className="text-left border-b-2 border-[#f9fafb]">
                  <th className="py-3 px-4">
                    <input
                      type="checkbox"
                      checked={allChecked}
                      onChange={toggleAll}
                    />
                  </th>
                  <th className="py-3 px-4 text-slate-700">Document</th>
                  <th className="py-3 px-4 text-slate-700">Type</th>
                  <th className="py-3 px-4 text-slate-700">Size</th>
                  <th className="py-3 px-4 text-slate-700">User</th>
                  <th className="py-3 px-4 text-slate-700">Date</th>
                  <th className="py-3 px-4 text-slate-700 text-center">
                    Status
                  </th>
                </tr>
              </thead>
              <tbody>
                {filteredFiles.map((file) => {
                  const isDisabled = !["ready", "failed"].includes(
                    file.status.toLowerCase()
                  );
                  const suffixedId = withSuffix(file.id);
                  const isChecked = checkedItems.includes(suffixedId);

                  return (
                    <tr
                      key={file.id}
                      className={`transition-colors duration-200 border-b-2 border-[#f9fafb] group ${
                        isDisabled
                          ? "bg-gray-100 text-gray-400 cursor-wait opacity-60"
                          : hoveredRow === suffixedId || isChecked
                          ? "bg-[#f9fafb]"
                          : "bg-white"
                      }`}
                      onMouseEnter={() =>
                        !isDisabled && setHoveredRow(suffixedId)
                      }
                      onMouseLeave={() => !isDisabled && setHoveredRow(null)}
                      onClick={(e) => {
                        if (!isDisabled) {
                          // Row click toggles selection using SUFFIXED id
                          toggleOne(suffixedId);
                        } else {
                          e.stopPropagation();
                        }
                      }}
                    >
                      <td className="py-3 px-4">
                        {(hoveredRow === suffixedId || isChecked) && (
                          <input
                            type="checkbox"
                            checked={isChecked}
                            onChange={(e) => {
                              e.stopPropagation();
                              toggleOne(suffixedId);
                            }}
                          />
                        )}
                      </td>
                      <td
                        className={`py-3 px-4 ${isChecked ? "font-bold" : ""}`}
                      >
                        <div className="flex text-slate-700 items-center my-1 space-x-3">
                          {loadingFileId === `${file.id}` ? (
                            <Loader color="#7da4f8" />
                          ) : (
                            <img
                              src={getFileIcon(file.filetype)}
                              alt="icon"
                              className="w-7 h-7"
                            />
                          )}
                          <div className="relative flex-1">
                            <div
                              className={`flex space-x-2 truncate w-full ${
                                isDisabled
                                  ? "cursor-wait text-gray-400"
                                  : "cursor-pointer hover:underline"
                              }`}
                              onClick={(e) => {
                                e.stopPropagation();
                                if (!isDisabled) {
                                  loadFile(
                                    file.id,
                                    file.filetype,
                                    file.size,
                                    file.filename
                                  );
                                }
                              }}
                            >
                              <span className="truncate">{file.filename}</span>
                            </div>
                            {isDisabled && (
                              <div className="absolute bottom-full left-[40px] mb-1 hidden group-hover:block z-20">
                                <div className="bg-gray-900 text-white text-xs rounded px-2 py-1 whitespace-nowrap shadow-lg">
                                  Processing
                                </div>
                              </div>
                            )}
                          </div>
                        </div>
                      </td>
                      <td className="py-3 px-4 text-slate-700">
                        {file.filetype}
                      </td>
                      <td className="py-3 px-4 text-slate-700">
                        {(file.size / 1024).toFixed(2)} KB
                      </td>
                      <td className="py-3 px-4 text-slate-700">
                        {file.owner?.first_name} {file.owner?.last_name}
                      </td>
                      <td className="py-3 px-4 text-slate-700">
                        {file.uploaded_at
                          ? normalizeDate(file.uploaded_at)
                          : ""}
                      </td>
                      <td className="py-3 px-4 text-slate-700">
                        <div className="flex justify-center items-center">
                          {file.status === "failed" ? (
                            <Tooltip
                              content="File Processing Failed"
                              arrow={false}
                            >
                              <img
                                src="/status/x-circle.svg"
                                className="w-5 h-5 2xl:w-6 2xl:h-6"
                              />
                            </Tooltip>
                          ) : ["xlsx", "csv"].includes(
                              file.filetype.toLowerCase()
                            ) ? (
                            file.status === "ready" ? (
                              activeTaskFileIds.includes(file.id) ? (
                                <Tooltip
                                  content="Looking for Anomalies"
                                  arrow={false}
                                >
                                  <Loader color="#7da4f8" />
                                </Tooltip>
                              ) : (
                                <StatusIcon
                                  file={file}
                                  handleShowPopup={handleShowPopup}
                                  triggerStatusPopup={triggerStatusPopup}
                                  clearStatusPopup={clearStatusPopup}
                                />
                              )
                            ) : (
                              <Loader color="#7da4f8" />
                            )
                          ) : null}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </>
      )}

      <DeleteEntityModal
        isOpen={modalOpen}
        selectedEntity={folder}
        onClose={() => setModalOpen(false)}
        onConfirm={handleFolderDelete}
        detailsText={`This action will remove the link with the Google Drive folder. Are you sure?`}
        titleText="Are you sure you want to remove this folder"
        confirmText="Remove"
        isLoading={deleting}
      />
    </div>
  );
};

export default DriveFolderPreview;
